import { MapPin, Phone, ArrowUpRight } from "lucide-react";
import { BrandIcon } from "@/components/BrandIcons";

const SOCIALS = [
  {
    id: "social-instagram",
    icon: "instagram",
    label: "Instagram",
    user: "@azhanizaenterpr_",
    href: "https://instagram.com/azhanizaenterpr_",
  },
  {
    id: "social-threads",
    icon: "threads",
    label: "Threads",
    user: "@azhanizaenterpr_",
    href: "https://www.threads.net/@azhanizaenterpr_",
  },
  {
    id: "social-tiktok",
    icon: "tiktok",
    label: "TikTok",
    user: "@az.haniza.enterpr",
    href: "https://www.tiktok.com/@az.haniza.enterpr",
  },
];

export default function FooterSection({ onNavigate }) {
  return (
    <footer
      id="contact"
      data-testid="contact-footer"
      className="bg-charcoal-900 text-cream-50 pt-16 pb-10"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-12">
          <span className="eyebrow !text-taupe-400" data-testid="contact-eyebrow">
            Contact Us
          </span>
          <h2 className="mt-3 font-poppins font-semibold text-2xl sm:text-3xl">
            Get in touch with our team
          </h2>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12">
          <div>
            <div className="flex items-center gap-3">
              <img
                src="/assets/azh-logo.webp"
                alt="Az Haniza Enterprise logo"
                className="h-11 w-11 object-cover rounded-full border border-cream-50/20"
              />
              <span className="font-poppins font-semibold text-base tracking-wide">
                Az Haniza Enterprise
              </span>
            </div>
            <p className="mt-5 text-sm text-cream-50/60 leading-relaxed">
              Premium corporate supply and procurement, delivered with
              distinction across Malaysia.
            </p>
            <a
              data-testid="footer-whatsapp-button"
              href="https://wa.me/60143856809"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-6 inline-flex items-center gap-2 bg-[#25D366] text-white text-xs uppercase tracking-[0.15em] font-medium px-5 py-3 rounded-full hover:bg-[#1eb85a] transition-colors duration-200"
            >
              <BrandIcon name="whatsapp" className="h-4 w-4" />
              WhatsApp Us
            </a>
          </div>

          <div data-testid="contact-address-block">
            <h4 className="text-xs uppercase tracking-[0.25em] font-medium text-taupe-400">
              Factory Address
            </h4>
            <div className="mt-5 flex items-start gap-3">
              <MapPin className="h-4 w-4 mt-1 text-taupe-500 shrink-0" />
              <p className="text-sm text-cream-50/70 leading-relaxed">
                No.20, Pesiaran Perindustrian
                <br />
                Pengkalan 4, Kawasan
                <br />
                Perindustrian Pengkalan,
                <br />
                31500 Ipoh, Perak
              </p>
            </div>
          </div>

          <div data-testid="contact-phone-block">
            <h4 className="text-xs uppercase tracking-[0.25em] font-medium text-taupe-400">
              Phone
            </h4>
            <div className="mt-5 flex items-start gap-3">
              <Phone className="h-4 w-4 mt-1 text-taupe-500 shrink-0" />
              <div className="text-sm text-cream-50/70 leading-relaxed">
                <a
                  data-testid="contact-phone-link"
                  href="tel:+60105666809"
                  className="hover:text-taupe-400 transition-colors duration-200"
                >
                  010 566 6809
                </a>
                <p className="mt-2">
                  WhatsApp:{" "}
                  <a
                    data-testid="contact-whatsapp-link"
                    href="https://wa.me/60143856809"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-taupe-400 transition-colors duration-200"
                  >
                    014 385 6809
                  </a>
                </p>
              </div>
            </div>
          </div>

          <div data-testid="contact-social-block">
            <h4 className="text-xs uppercase tracking-[0.25em] font-medium text-taupe-400">
              Follow Us
            </h4>
            <ul className="mt-5 space-y-4">
              {SOCIALS.map((s) => (
                <li key={s.id}>
                  <a
                    data-testid={s.id}
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex items-center gap-3"
                  >
                    <span className="flex h-9 w-9 items-center justify-center rounded-full border border-cream-50/20 text-cream-50/70 group-hover:text-taupe-400 group-hover:border-taupe-400 transition-colors duration-200">
                      <BrandIcon name={s.icon} className="h-4 w-4" />
                    </span>
                    <span className="text-sm text-cream-50/70 group-hover:text-cream-50 transition-colors duration-200">
                      <span className="text-[10px] uppercase tracking-[0.15em] text-cream-50/40 block">
                        {s.label}
                      </span>
                      {s.user}
                    </span>
                  </a>
                </li>
              ))}
            </ul>
            <button
              data-testid="footer-order-button"
              onClick={() => onNavigate("order")}
              className="mt-6 group flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-medium text-taupe-400 hover:text-cream-50 transition-colors duration-200"
            >
              Place an Order
              <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-cream-50/10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-cream-50/50" data-testid="footer-copyright">
            © {new Date().getFullYear()} Az Haniza Enterprise. All rights reserved.
          </p>
          <p className="text-xs text-cream-50/40 tracking-[0.15em] uppercase">
            Sourcing Excellence · Delivered with Distinction
          </p>
        </div>
      </div>
    </footer>
  );
}
